// src/modules/supplier/SupplierDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FaBuilding, 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaMapMarkerAlt,
  FaBox, 
  FaTags, 
  FaStickyNote,
  FaEdit, 
  FaTrash, 
  FaArrowLeft,
  FaDirections
} from 'react-icons/fa';
import { getSupplier, deleteSupplier } from './supplierService';
import DashboardLayout from '../../layouts/DashboardLayout';
import Map from '../../components/shared/Map';
import Modal from '../../components/shared/Modal';
import useModal from '../../hooks/useModal';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import InfoCard from '../../components/shared/InfoCard';
import {
  DetailContainer,
  DetailHeader,
  DetailTitle,
  DetailField,
  ActionBar,
  ActionButton,
  DetailGrid
} from '../../components/styled/DetailComponents';

const SupplierDetails = () => {

  const { id } = useParams();  // Get supplier ID from URL
  const [supplier, setSupplier] = useState(null);
  const { isVisible, showModal, hideModal, confirmAction } = useModal();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchSupplier = async () => {
      const data = await getSupplier(id);  // Fetch supplier data by ID
      setSupplier(data);
    };
    fetchSupplier();
  }, [id]);

  const handleDelete = () => {
    showModal(() => handleConfirmDelete);
  };

  const handleConfirmDelete = async () => {
    await deleteSupplier(id);  // Call API to delete supplier, then navigate back to list
    navigate('/suppliers');
  };

  // Use LoadingSpinner component while waiting for data
  if (!supplier) return <LoadingSpinner />;

  // Custom pin icon URL (optional)
  const customPinUrl = "http://maps.google.com/mapfiles/ms/icons/blue-dot.png";
  if (!supplier) return <LoadingSpinner />;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${supplier.latitude},${supplier.longitude}`;

  return (
    <DashboardLayout>
      <DetailHeader>
        <DetailTitle>
          <FaBuilding size={24} />
          <h1>{supplier.name}</h1>
        </DetailTitle>
        
        <ActionBar>
          <ActionButton onClick={() => navigate('/suppliers')}>
            <FaArrowLeft /> Back
          </ActionButton>
          <ActionButton onClick={() => window.open(`tel:${supplier.phone}`)}>
            <FaPhone /> Call
          </ActionButton>
          <ActionButton onClick={() => window.open(directionsUrl, '_blank')}>
            <FaDirections /> Directions
          </ActionButton>
          <ActionButton 
            variant="primary"
            onClick={() => navigate(`/suppliers/edit/${supplier.id}`)}
          >
            <FaEdit /> Edit
          </ActionButton>
          <ActionButton variant="danger" onClick={handleDelete}>
            <FaTrash /> Delete
          </ActionButton>
        </ActionBar>
      </DetailHeader>

      <DetailContainer>
        <InfoCard title="Contact Information" icon={<FaUser />}>
          <DetailGrid>
            <DetailField>
              <label>Contact Person</label>
              <span>{supplier.contactPerson}</span>
            </DetailField>
            <DetailField>
              <label>Email</label>
              <span>{supplier.email}</span>
            </DetailField>
            <DetailField>
              <label>Phone</label>
              <span>{supplier.phone}</span>
            </DetailField>
          </DetailGrid>
        </InfoCard>

        <InfoCard title="Categories" icon={<FaTags />}>
          <DetailField>
            <span>
              {supplier.categories.map(category => category.name).join(', ') || 'No categories assigned'}
            </span>
          </DetailField>
        </InfoCard>

        <InfoCard title="Products" icon={<FaBox />}>
          {supplier.products.length === 0 ? (
            <DetailField>No products available.</DetailField>
          ) : (
            supplier.products.map((product) => (
              <DetailField key={product.id}>
                <label>{product.name}</label>
                <span>${product.SupplierProduct.unitPrice}/{product.unit}</span>
              </DetailField>
            ))
          )}
        </InfoCard>        

        {supplier.notes && (
          <InfoCard title="Notes" icon={<FaStickyNote />}>
            <DetailField>
              <span>{supplier.notes}</span>
            </DetailField>
          </InfoCard>
        )}

<InfoCard title="Location" icon={<FaMapMarkerAlt />}>
          <DetailField>
            <label>Address</label>
            <span>
              {supplier.address}, {supplier.city}, {supplier.state} {supplier.zipCode}
            </span>
          </DetailField>
          <Map
            addresses={[{ lat: supplier.latitude, lng: supplier.longitude }]}
            zoom={14}
            height="200px"
          />
        </InfoCard>
        
      </DetailContainer>

      {isVisible && (
        <Modal
          title="Delete Supplier"
          message="Are you sure you want to delete this supplier? This action cannot be undone."
          onConfirm={confirmAction}
          onCancel={hideModal}
        />
      )}
    </DashboardLayout>
  );
};

export default SupplierDetails;